import { useState, useRef, useCallback } from 'preact/hooks';

interface MediaRecorderErrorEvent extends Event {
  error?: DOMException;
}

const MEMO_HARD_CAP_MS = 60 * 1000; // 1 min — Whisper OOM guard

export function useMemoRecorder() {
  const [recording, setRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);
  const userStoppedRef = useRef(false);
  const stopResolveRef = useRef<((result: { chunks: Blob[]; durationMs: number }) => void) | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (visibilityCleanupRef.current) {
      visibilityCleanupRef.current();
      visibilityCleanupRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.onstatechange = null;
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
      sourceRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }
      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      userStoppedRef.current = false;
      stopResolveRef.current = null;
      setDurationMs(0);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onerror = (e: Event) => {
        const err = e as MediaRecorderErrorEvent;
        console.error('[useMemoRecorder] MediaRecorder error:', err?.error?.name, err?.error?.message);
        cleanup();
        if (recorder.state !== 'inactive') {
          try { recorder.stop(); } catch { /* already stopped */ }
        }
      };

      recorder.onstop = () => {
        const elapsed = Date.now() - startTimeRef.current;
        if (!userStoppedRef.current) {
          console.warn(`[useMemoRecorder] Unexpected stop after ${(elapsed / 1000).toFixed(1)}s with ${chunksRef.current.length} chunks`);
        }
        recorder.stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const resolve = stopResolveRef.current;
        stopResolveRef.current = null;
        if (resolve) {
          resolve({ chunks: chunksRef.current, durationMs: elapsed });
        }
      };

      audioCtx.onstatechange = () => {
        if (audioCtx.state === 'suspended' && recorder.state === 'recording') {
          console.warn('[useMemoRecorder] AudioContext suspended mid-recording, resuming');
          audioCtx.resume().catch(() => {});
        }
      };

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && audioCtx.state === 'suspended') {
          console.warn('[useMemoRecorder] Page visible again, resuming AudioContext');
          audioCtx.resume().catch(() => {});
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      visibilityCleanupRef.current = () => document.removeEventListener('visibilitychange', onVisibilityChange);

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          if (recorder.state === 'recording') {
            console.warn('[useMemoRecorder] Audio track ended unexpectedly');
            cleanup();
            try { recorder.stop(); } catch { /* already stopped */ }
          }
        };
        audioTrack.onmute = () => console.warn('[useMemoRecorder] Audio track muted');
        audioTrack.onunmute = () => console.warn('[useMemoRecorder] Audio track unmuted');
      }

      recorder.start(250);
      mediaRef.current = recorder;
      setRecording(true);

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDurationMs(elapsed);
        if (elapsed >= MEMO_HARD_CAP_MS && mediaRef.current?.state === 'recording') {
          userStoppedRef.current = true;
          cleanup();
          try { mediaRef.current.stop(); } catch { /* already stopped */ }
        }
      }, 100);
    } catch (err: any) {
      console.error('Mic access denied:', err);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      setMicError(isDenied
        ? 'Microphone access denied. Allow mic in browser settings and try again.'
        : 'Could not start recording. Check your microphone connection.');
    }
  }, [cleanup]);

  const stop = useCallback((): Promise<{ chunks: Blob[]; durationMs: number }> => {
    return new Promise((resolve) => {
      userStoppedRef.current = true;
      cleanup();
      const recorder = mediaRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setRecording(false);
        resolve({ chunks: chunksRef.current, durationMs: Date.now() - startTimeRef.current });
        return;
      }
      stopResolveRef.current = resolve;
      recorder.stop();
    });
  }, [cleanup]);

  const clearMicError = useCallback(() => setMicError(null), []);

  return { recording, durationMs, start, stop, micError, clearMicError };
}
